import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { ArrowLeft, Save, User, FileText, Award, Target, Users, AlertTriangle, ExternalLink } from 'lucide-react';
import { PersonalTab, DocumentsTab, FamilyTab, TravelTab, LeadTab } from '@/components/AddTraveller';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Display-layer PII masking: fixed-length '****' prefix + last 4 chars.
// Used only in list/quick-preview surfaces (here: the duplicate-match modal).
const maskPII = (value) => {
  const s = String(value || '').trim();
  if (s.length <= 4) return s;
  return '****' + s.slice(-4);
};

const AddTraveller = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [dupTraveller, setDupTraveller] = useState(null);
  
  const [formData, setFormData] = useState({
    fullName: '',
    givenName: '',
    surname: '',
    companyName: '',
    companyGstNumber: '',
    companyAddress: '',
    email: '',
    phone: '',
    countryCode: '+91',
    gender: '',
    nationality: '',
    maritalStatus: '',
    numberOfKids: 0,
    dateOfBirth: '',
    marriageAnniversary: '',
    address: '',
    pincode: '',
    passportNumber: '',
    passportIssueDate: '',
    passportExpiryDate: '',
    passportIssuedAt: '',
    panCard: '',
    aadharCard: '',
    travelCategory: 'budget',
    leadSource: '',
    leadGeneratedDate: new Date().toISOString().split('T')[0],
    referredBy: '',
    familyMembers: []
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const doCreate = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/clients`, formData);
      toast.success(`Traveller created! UCC: ${response.data.ucc}`);
      navigate('/clients');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create traveller');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const pn = (formData.passportNumber || '').trim();
    const ph = (formData.phone || '').trim();
    if (pn || ph) {
      setLoading(true);
      try {
        const res = await axios.get(`${BACKEND_URL}/api/clients/check-duplicate`, { params: { passportNumber: pn, phone: ph } });
        if (res.data?.exists && res.data?.traveller) {
          setDupTraveller({ ...res.data.traveller, matchedBy: res.data.matchedBy });
          setLoading(false);
          return;
        }
      } catch {
        /* check failed — don't block creation */
      }
      setLoading(false);
    }
    await doCreate();
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <div className="flex items-center space-x-4">
        <Button variant="ghost" onClick={() => navigate('/clients')} data-testid="back-to-travellers-btn">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Travellers
        </Button>
      </div>

      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-600/5 to-accent-600/5 rounded-3xl blur-3xl"></div>
        <div className="relative">
          <h1 className="text-4xl lg:text-5xl font-heading font-bold text-slate-900 tracking-tight mb-3">
            Add New Traveller
          </h1>
          <p className="text-base lg:text-lg text-slate-600 font-light">Create a comprehensive traveller profile</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <Tabs defaultValue="personal" className="w-full">
          <TabsList className="grid w-full grid-cols-5 mb-10 h-14 bg-white/80 backdrop-blur-xl border border-slate-200/60 rounded-2xl p-1.5 shadow-sm">
            <TabsTrigger value="personal" data-testid="tab-personal" className="rounded-xl data-[state=active]:bg-gradient-to-br data-[state=active]:from-primary-700 data-[state=active]:to-primary-600 data-[state=active]:text-white data-[state=active]:shadow-lg transition-all text-xs sm:text-sm">
              <User className="h-4 w-4 mr-1 sm:mr-2" /><span className="hidden sm:inline">Personal</span>
            </TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents" className="rounded-xl data-[state=active]:bg-gradient-to-br data-[state=active]:from-primary-700 data-[state=active]:to-primary-600 data-[state=active]:text-white data-[state=active]:shadow-lg transition-all text-xs sm:text-sm">
              <FileText className="h-4 w-4 mr-1 sm:mr-2" /><span className="hidden sm:inline">Documents</span>
            </TabsTrigger>
            <TabsTrigger value="family" data-testid="tab-family" className="rounded-xl data-[state=active]:bg-gradient-to-br data-[state=active]:from-primary-700 data-[state=active]:to-primary-600 data-[state=active]:text-white data-[state=active]:shadow-lg transition-all text-xs sm:text-sm">
              <Users className="h-4 w-4 mr-1 sm:mr-2" /><span className="hidden sm:inline">Family</span>
            </TabsTrigger>
            <TabsTrigger value="travel" data-testid="tab-travel" className="rounded-xl data-[state=active]:bg-gradient-to-br data-[state=active]:from-primary-700 data-[state=active]:to-primary-600 data-[state=active]:text-white data-[state=active]:shadow-lg transition-all text-xs sm:text-sm">
              <Award className="h-4 w-4 mr-1 sm:mr-2" /><span className="hidden sm:inline">Travel</span>
            </TabsTrigger>
            <TabsTrigger value="lead" data-testid="tab-lead" className="rounded-xl data-[state=active]:bg-gradient-to-br data-[state=active]:from-primary-700 data-[state=active]:to-primary-600 data-[state=active]:text-white data-[state=active]:shadow-lg transition-all text-xs sm:text-sm">
              <Target className="h-4 w-4 mr-1 sm:mr-2" /><span className="hidden sm:inline">Lead</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="personal" className="animate-fade-in">
            <PersonalTab formData={formData} handleChange={handleChange} handleSelectChange={handleSelectChange} />
          </TabsContent>

          <TabsContent value="documents" className="animate-fade-in">
            <DocumentsTab formData={formData} handleChange={handleChange} handleSelectChange={handleSelectChange} />
          </TabsContent>

          <TabsContent value="family" className="animate-fade-in">
            <FamilyTab formData={formData} setFormData={setFormData} />
          </TabsContent>

          <TabsContent value="travel" className="animate-fade-in">
            <TravelTab formData={formData} handleSelectChange={handleSelectChange} />
          </TabsContent>

          <TabsContent value="lead" className="animate-fade-in">
            <LeadTab formData={formData} handleChange={handleChange} handleSelectChange={handleSelectChange} />
          </TabsContent>
        </Tabs>

        <div className="flex justify-end space-x-4 mt-10">
          <Button type="button" variant="outline" onClick={() => navigate('/clients')} data-testid="cancel-btn" className="px-8 py-3 h-12 rounded-xl">Cancel</Button>
          <Button type="submit" disabled={loading} data-testid="create-traveller-btn" className="bg-gradient-to-br from-primary-700 to-primary-600 text-white shadow-lg px-10 py-3 h-12 rounded-xl font-semibold">
            <Save className="h-5 w-5 mr-2" />
            {loading ? 'Creating...' : 'Create Traveller'}
          </Button>
        </div>
      </form>

      {dupTraveller ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="dup-passport-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  {dupTraveller.matchedBy === 'phone' ? 'Mobile number already exists' : 'Passport already exists'}
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  A traveller with {dupTraveller.matchedBy === 'phone'
                    ? <>mobile <span className="font-semibold">{dupTraveller.phone}</span></>
                    : <>passport <span className="font-semibold" data-testid="dup-passport-masked">{maskPII(dupTraveller.passportNumber)}</span></>}
                  {' '}already exists. Creating another may produce a duplicate profile.
                </p>
              </div>
            </div>

            <a
              href={`/clients/${dupTraveller.ucc}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="dup-passport-existing-link"
              className="flex items-center justify-between gap-3 border border-slate-200 rounded-xl px-4 py-3 hover:bg-slate-50 transition-colors"
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate">{dupTraveller.fullName || 'Existing traveller'}</p>
                <p className="text-xs text-slate-500">{dupTraveller.displayUCC || dupTraveller.ucc}</p>
              </div>
              <ExternalLink className="h-4 w-4 text-primary-600 shrink-0" />
            </a>

            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setDupTraveller(null)} data-testid="dup-passport-cancel-btn" className="rounded-xl">
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => { setDupTraveller(null); doCreate(); }}
                data-testid="dup-passport-create-anyway-btn"
                className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl"
              >
                Create anyway
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AddTraveller;
